(function (global) {
  'use strict';

  const STATUS = Object.freeze({
    AUTO: 'AUTO_SUPPORTED',
    REQUIRED: 'AUTO_SUPPORTED_WITH_REQUIRED_INPUTS',
    REVIEW: 'MANUAL_REVIEW',
    UNSUPPORTED: 'UNSUPPORTED'
  });

  const STATUS_PRIORITY = Object.freeze({
    [STATUS.AUTO]: 0,
    [STATUS.REQUIRED]: 1,
    [STATUS.REVIEW]: 2,
    [STATUS.UNSUPPORTED]: 3
  });

  const PRIMARY_DNS = Object.freeze([130, 150, 160, 180, 200]);
  const WALL_DISTANCES_MM = Object.freeze([50, 100, 150, 200, 250, 300, 360]);
  const FREE_HEIGHTS_M = Object.freeze([2]);
  const FIXED_HEIGHT_MM = 1265;
  const FIRST_HOLDER_REFERENCE_MM = 1110;
  const MAX_STANDARD_HOLDER_SPACING_M = 4;

  const HEIGHT_RECIPES = Object.freeze([
    { targetMm: 2209, counts: { DW13: 1, DW14: 0, DW15: 0 } },
    { targetMm: 2653, counts: { DW13: 1, DW14: 1, DW15: 0 } },
    { targetMm: 2847, counts: { DW13: 1, DW14: 1, DW15: 1 } },
    { targetMm: 3041, counts: { DW13: 1, DW14: 1, DW15: 2 } },
    { targetMm: 3153, counts: { DW13: 2, DW14: 0, DW15: 0 } },
    { targetMm: 4097, counts: { DW13: 3, DW14: 0, DW15: 0 } },
    { targetMm: 5041, counts: { DW13: 4, DW14: 0, DW15: 0 } },
    { targetMm: 5985, counts: { DW13: 5, DW14: 0, DW15: 0 } },
    { targetMm: 6929, counts: { DW13: 6, DW14: 0, DW15: 0 } },
    { targetMm: 7873, counts: { DW13: 7, DW14: 0, DW15: 0 } },
    { targetMm: 8817, counts: { DW13: 8, DW14: 0, DW15: 0 } }
  ]);

  const COMPONENT_LABELS = Object.freeze({
    'DW-JGP01': 'Offizielles DW-FU Grundpaket DN 150',
    DW05: 'Grundplatte mit Kondensatablauf unten',
    DW10: 'Revisionselement',
    DW317: 'T-Anschluss 90°',
    DW32: 'Mündungsabschluss',
    DW31: 'Wetterkragen / Wandrosette',
    DW13: 'Längenelement 1.000 mm (Einbaumaß 944 mm)',
    DW14: 'Längenelement 500 mm (Einbaumaß 444 mm)',
    DW15: 'Längenelement 250 mm (Einbaumaß 194 mm)',
    DW21: 'Wandhalter 50 mm',
    DW22: 'Wandhalter bis 150 mm',
    DW23: 'Wandhalter bis 250 mm',
    DW24: 'Wandhalter bis 360 mm',
    DW01: 'Konsolbleche bis 150 mm',
    DW02: 'Wandkonsole bis 250 mm',
    DW49: 'Wandkonsole bis 360 mm',
    DW391: 'Wandstütze und Querträger Typ I',
    DW392: 'Wandstütze und Querträger Typ II',
    DW393: 'Wandstütze und Querträger Typ III',
    DW407: 'Wandstütze und Querträger Typ IV'
  });

  const CROSSBEAM_LIMITS_MM = Object.freeze({
    130: Object.freeze({ DW391: 140, DW392: 290, DW393: 540, DW407: 820 }),
    150: Object.freeze({ DW391: 120, DW392: 270, DW393: 520, DW407: 800 }),
    180: Object.freeze({ DW391: 90, DW392: 240, DW393: 490, DW407: 770 }),
    200: Object.freeze({ DW391: 70, DW392: 220, DW393: 470, DW407: 750 })
  });

  function highestStatus(statuses) {
    return statuses.reduce((highest, status) => {
      return STATUS_PRIORITY[status] > STATUS_PRIORITY[highest] ? status : highest;
    }, STATUS.AUTO);
  }

  function addReason(reasons, code, message, status, step) {
    reasons.push({ code, message, status, step });
    return status;
  }

  function getHeightRecipe(targetMm) {
    return HEIGHT_RECIPES.find((recipe) => recipe.targetMm === Number(targetMm)) || null;
  }

  function calculateHeightMm(counts) {
    return FIXED_HEIGHT_MM
      + (Number(counts.DW13) || 0) * 944
      + (Number(counts.DW14) || 0) * 444
      + (Number(counts.DW15) || 0) * 194;
  }

  function selectWallHolder(distanceMm) {
    const distance = Number(distanceMm);
    if (distance === 50) return 'DW21';
    if (distance > 50 && distance <= 150) return 'DW22';
    if (distance > 150 && distance <= 250) return 'DW23';
    if (distance > 250 && distance <= 360) return 'DW24';
    return null;
  }

  function selectConsole(distanceMm) {
    const distance = Number(distanceMm);
    if (distance >= 50 && distance <= 150) return 'DW01';
    if (distance > 150 && distance <= 250) return 'DW02';
    if (distance > 250 && distance <= 360) return 'DW49';
    return null;
  }

  function selectCrossbeam(dn, distanceMm) {
    const numericDn = Number(dn);
    if (numericDn === 160) return { code: null, status: STATUS.REVIEW };
    const row = CROSSBEAM_LIMITS_MM[numericDn];
    if (!row) return { code: null, status: STATUS.UNSUPPORTED };
    const distance = Number(distanceMm);
    const code = Object.keys(row).find((candidate) => distance <= row[candidate]);
    return code
      ? { code, status: STATUS.AUTO, maximumMm: row[code] }
      : { code: null, status: STATUS.UNSUPPORTED };
  }

  function calculateHolderCount(targetHeightMm, freeHeightM) {
    const heightAboveMandatoryHolderM = Math.max(0, (Number(targetHeightMm) - FIRST_HOLDER_REFERENCE_MM) / 1000);
    const freeHeight = Number(freeHeightM);
    if (heightAboveMandatoryHolderM <= freeHeight) return 1;
    return 1 + Math.ceil((heightAboveMandatoryHolderM - freeHeight) / MAX_STANDARD_HOLDER_SPACING_M);
  }

  function defaultState() {
    return {
      system: 'DW-FU',
      use: 'solid-fuel',
      dn: 130,
      targetHeightMm: 4097,
      mounting: 'wall',
      supportType: 'console-plates',
      wallDistanceMm: 100,
      buildingRoute: 'facade',
      roofPitch: null,
      roofMaterial: null,
      freeHeightM: 2,
      anchorVerified: false,
      chimneySweepConfirmed: false
    };
  }

  function normalizeState(input) {
    return Object.assign(defaultState(), input || {}, {
      dn: Number((input || {}).dn ?? defaultState().dn),
      targetHeightMm: Number((input || {}).targetHeightMm ?? defaultState().targetHeightMm),
      wallDistanceMm: Number((input || {}).wallDistanceMm ?? defaultState().wallDistanceMm),
      freeHeightM: Number((input || {}).freeHeightM ?? defaultState().freeHeightM)
    });
  }

  function skuFor(code, dn) {
    if (code === 'DW-JGP01') return 'DW-JGP01150';
    return ['DW391', 'DW392', 'DW393', 'DW407'].includes(code) ? code : `${code}${dn}`;
  }

  function resolveConfiguration(inputState, catalog) {
    const state = normalizeState(inputState);
    const reasons = [];
    const statuses = [];
    const recipe = getHeightRecipe(state.targetHeightMm);
    const logicalLines = [];
    const pendingLines = [];
    const liveCatalog = catalog || {};

    if (state.system !== 'DW-FU') {
      statuses.push(addReason(reasons, 'SYSTEM_OUTSIDE_V1', 'V1 unterstützt ausschließlich Jeremias DW-FU.', STATUS.UNSUPPORTED, 1));
    }
    if (!PRIMARY_DNS.includes(state.dn)) {
      statuses.push(addReason(reasons, 'DN_OUTSIDE_V1', 'Dieser Nenndurchmesser liegt außerhalb des V1-Pilotumfangs.', STATUS.UNSUPPORTED, 2));
    }
    if (!recipe || calculateHeightMm(recipe ? recipe.counts : {}) !== state.targetHeightMm) {
      statuses.push(addReason(reasons, 'HEIGHT_NOT_EXACT', 'Die Zielhöhe ist mit den freigegebenen Einbaumaßen nicht exakt darstellbar.', STATUS.UNSUPPORTED, 3));
    }
    if (!WALL_DISTANCES_MM.includes(state.wallDistanceMm)) {
      statuses.push(addReason(reasons, 'WALL_DISTANCE_OUTSIDE_V1', 'Der Wandabstand liegt außerhalb der dokumentierten V1-Auswahl.', STATUS.UNSUPPORTED, 4));
    }
    if (!FREE_HEIGHTS_M.includes(state.freeHeightM)) {
      statuses.push(addReason(reasons, 'FREE_HEIGHT_OUTSIDE_V1', 'Die freie Höhe liegt außerhalb des fest definierten V1-Standardwerts von 2 m.', STATUS.UNSUPPORTED, 5));
    }

    if (state.dn === 150) {
      logicalLines.push({
        code: 'DW-JGP01',
        quantity: 1,
        rule: 'DN150_USE_OFFICIAL_BASE_PACKAGE',
        componentKind: 'official-package'
      });
    } else if (PRIMARY_DNS.includes(state.dn)) {
      ['DW05', 'DW10', 'DW317', 'DW32', 'DW31'].forEach((code) => logicalLines.push({
        code,
        quantity: 1,
        rule: 'NON_DN150_BUILD_FIVE_DOCUMENTED_BASE_ROLES',
        componentKind: 'single-component'
      }));
    }

    if (recipe) {
      Object.entries(recipe.counts).forEach(([code, quantity]) => {
        if (quantity > 0) logicalLines.push({
          code,
          quantity,
          rule: 'EXACT_STANDARD_HEIGHT_RECIPE',
          componentKind: 'single-component'
        });
      });
    }

    if (state.mounting === 'floor') {
      statuses.push(addReason(reasons, 'FLOOR_SUPPORT_REQUIRES_PROJECT_SELECTION', 'Sockel, Fundament und Stütze müssen projektbezogen geprüft werden.', STATUS.REVIEW, 4));
    } else if (state.mounting === 'wall') {
      const holderCode = selectWallHolder(state.wallDistanceMm);
      if (!holderCode) {
        statuses.push(addReason(reasons, 'NO_WALL_HOLDER', 'Für den Wandabstand ist kein Standard-Wandhalter freigegeben.', STATUS.UNSUPPORTED, 4));
      } else {
        logicalLines.push({
          code: holderCode,
          quantity: calculateHolderCount(state.targetHeightMm, state.freeHeightM),
          rule: 'MANDATORY_HOLDER_ABOVE_T_PLUS_MAX_4M_SPACING',
          componentKind: 'single-component'
        });
      }

      if (state.supportType === 'crossbeam-support') {
        const support = selectCrossbeam(state.dn, state.wallDistanceMm);
        statuses.push(support.status);
        if (support.status === STATUS.REVIEW) {
          addReason(reasons, 'DN160_CROSSBEAM_CONTRADICTION', 'Die veröffentlichte DN-160-Tabelle widerspricht der Jeremias-Formel; die Stütze wird nicht automatisch gewählt.', STATUS.REVIEW, 4);
        } else if (support.status === STATUS.UNSUPPORTED) {
          addReason(reasons, 'NO_CROSSBEAM_SUPPORT', 'Für DN und Wandabstand ist in V1 keine passende Querträger-Stütze vorhanden.', STATUS.UNSUPPORTED, 4);
        } else {
          logicalLines.push({
            code: support.code,
            quantity: 1,
            rule: 'DN_SPECIFIC_CROSSBEAM_DISTANCE_LOOKUP',
            componentKind: 'single-component'
          });
        }
      } else if (state.supportType === 'console-plates') {
        const consoleCode = selectConsole(state.wallDistanceMm);
        if (consoleCode) logicalLines.push({
          code: consoleCode,
          quantity: 1,
          rule: 'STANDARD_CONSOLE_DISTANCE_RANGE',
          componentKind: 'single-component'
        });
        else statuses.push(addReason(reasons, 'NO_CONSOLE', 'Für den Wandabstand ist keine Standardkonsole freigegeben.', STATUS.UNSUPPORTED, 4));
      } else {
        statuses.push(addReason(reasons, 'UNKNOWN_SUPPORT_TYPE', 'Die gewählte Wandbefestigung ist in V1 nicht definiert.', STATUS.UNSUPPORTED, 4));
      }
    } else {
      statuses.push(addReason(reasons, 'UNKNOWN_MOUNTING', 'Die Montageart ist in V1 nicht definiert.', STATUS.UNSUPPORTED, 4));
    }

    if (state.buildingRoute === 'wall-penetration') {
      statuses.push(addReason(reasons, 'WALL_PENETRATION_REVIEW', 'Wandaufbau, Brandschutz und Anschlussstück müssen manuell geprüft werden.', STATUS.REVIEW, 5));
    } else if (state.buildingRoute === 'roof-penetration') {
      statuses.push(addReason(reasons, 'ROOF_CONSTRUCTION_REVIEW', 'Die Dachdurchführung kann nur vorgewählt werden; Konstruktion und Austrittshöhe benötigen Freigabe.', STATUS.REVIEW, 5));
    } else if (state.buildingRoute !== 'facade') {
      statuses.push(addReason(reasons, 'UNKNOWN_BUILDING_ROUTE', 'Der Gebäudeverlauf ist in V1 nicht definiert.', STATUS.UNSUPPORTED, 5));
    }

    if (state.freeHeightM !== 2) {
      statuses.push(addReason(reasons, 'SPECIAL_STATICS_BOM_REVIEW', 'Für eine von 2 m abweichende freie Höhe fehlen vollständige freigegebene V1-Regeln.', STATUS.REVIEW, 5));
      if (state.dn === 160 && state.freeHeightM > 3) {
        statuses.push(addReason(reasons, 'DN160_STATICS_ROW_MISSING', 'Für DN 160 fehlt zusätzlich die Hersteller-Kraftzeile.', STATUS.REVIEW, 5));
      }
    }

    if (!state.anchorVerified) {
      statuses.push(addReason(reasons, 'ANCHOR_INPUT_REQUIRED', 'Befestigungsmittel und Tragfähigkeit des Untergrunds müssen bestätigt werden.', STATUS.REQUIRED, 5));
    }
    if (!state.chimneySweepConfirmed) {
      statuses.push(addReason(reasons, 'CHIMNEY_SWEEP_INPUT_REQUIRED', 'Die Abstimmung mit dem bevollmächtigten Schornsteinfeger muss bestätigt werden.', STATUS.REQUIRED, 5));
    }

    const deduped = new Map();
    logicalLines.forEach((line) => {
      const key = `${line.code}:${state.dn}`;
      const existing = deduped.get(key);
      if (existing) existing.quantity += line.quantity;
      else deduped.set(key, Object.assign({}, line));
    });

    const bom = [];
    deduped.forEach((line) => {
      const sku = skuFor(line.code, state.dn);
      const variant = liveCatalog[sku];
      if (!variant || !variant.id || !Number.isFinite(Number(variant.price))) {
        statuses.push(addReason(reasons, 'LIVE_VARIANT_MISSING', `Shopify-Variante ${sku} ist nicht vollständig verfügbar.`, STATUS.REVIEW, 6));
        bom.push({
          code: line.code,
          articleCode: line.code,
          sku,
          system: 'DW-FU',
          dn: ['DW391', 'DW392', 'DW393', 'DW407'].includes(line.code) ? null : state.dn,
          quantity: line.quantity,
          label: COMPONENT_LABELS[line.code] || line.code,
          rule: line.rule,
          componentKind: line.componentKind,
          price: null,
          linePrice: null,
          variantId: null,
          productId: null,
          available: false,
          pending: true
        });
        return;
      }
      if (variant.available === false) {
        statuses.push(addReason(reasons, 'LIVE_VARIANT_UNAVAILABLE', `Shopify-Variante ${sku} ist derzeit nicht verfügbar.`, STATUS.REVIEW, 6));
      }
      bom.push({
        code: line.code,
        articleCode: line.code,
        sku,
        system: 'DW-FU',
        dn: ['DW391', 'DW392', 'DW393', 'DW407'].includes(line.code) ? null : state.dn,
        quantity: line.quantity,
        label: COMPONENT_LABELS[line.code] || variant.productTitle || line.code,
        rule: line.rule,
        componentKind: line.componentKind,
        price: Number(variant.price),
        linePrice: Number(variant.price) * line.quantity,
        variantId: Number(variant.id),
        productId: variant.productId ? Number(variant.productId) : null,
        available: variant.available !== false,
        pending: false
      });
    });

    const status = highestStatus(statuses);
    const hasUnknownPrice = pendingLines.length > 0 || bom.some((line) => line.price === null);
    const totalPrice = hasUnknownPrice ? null : bom.reduce((sum, line) => sum + line.linePrice, 0);
    const cartEligible = status === STATUS.AUTO
      && pendingLines.length === 0
      && bom.length > 0
      && bom.every((line) => line.variantId && line.available && line.price !== null);

    return {
      state,
      status,
      reasons,
      height: recipe ? { targetMm: recipe.targetMm, counts: Object.assign({}, recipe.counts), exact: true } : { targetMm: state.targetHeightMm, counts: null, exact: false },
      holderCount: state.mounting === 'wall' && recipe ? calculateHolderCount(state.targetHeightMm, state.freeHeightM) : 0,
      bom,
      pendingLines,
      totalPrice,
      cartEligible
    };
  }

  function buildCartPayload(result, configurationId) {
    if (!result || !result.cartEligible) throw new Error('CONFIGURATION_NOT_CART_ELIGIBLE');
    const configId = configurationId || `dw-fu-${Date.now().toString(36)}`;
    return {
      items: result.bom.map((line) => ({
        id: line.variantId,
        quantity: line.quantity,
        properties: {
          '_Konfiguration': configId,
          'System': 'Jeremias DW-FU',
          'Nenndurchmesser': `DN ${result.state.dn}`,
          'Zielhöhe': `${result.state.targetHeightMm} mm`
        }
      }))
    };
  }

  const api = Object.freeze({
    STATUS,
    PRIMARY_DNS,
    WALL_DISTANCES_MM,
    FREE_HEIGHTS_M,
    HEIGHT_RECIPES,
    FIXED_HEIGHT_MM,
    FIRST_HOLDER_REFERENCE_MM,
    MAX_STANDARD_HOLDER_SPACING_M,
    CROSSBEAM_LIMITS_MM,
    defaultState,
    normalizeState,
    getHeightRecipe,
    calculateHeightMm,
    calculateHolderCount,
    selectWallHolder,
    selectConsole,
    selectCrossbeam,
    resolveConfiguration,
    buildCartPayload
  });

  global.JeremiasDwFuRules = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
